import React, {useMemo} from 'react';
import translate from '@/utils/translate';
import AutocompleteInput from '@/components/autocomplete-input';
import type {Option} from '@/components/autocomplete-input/AutocompleteInput';
import type {CampaignCreator} from '../../types/campaignCreator';

/**
 * How the creator value changed:
 * - `select` — the user picked a creator from the list.
 * - `clear` — the user pressed the clear (X) button.
 * - `searchTextEmptied` — the user deleted the search text, e.g. by
 *   backspacing over the pre-filled name. MUI reports this as a clear too, but
 *   it means "I'm typing a new name", not "remove the filter".
 */
export type CreatorChangeReason = 'select' | 'clear' | 'searchTextEmptied';

type CreatorAutocompleteProps = {
  testID?: string;
  campaignCreators: CampaignCreator[];
  selectedCreator: CampaignCreator | null;
  onChange: (
    creator: CampaignCreator | null,
    reason: CreatorChangeReason
  ) => void;
  openOnFocus?: boolean;
  autoFocus?: boolean;
  selectTextOnFocus?: boolean;
};

function CreatorAutocomplete({
  testID = 'creator-autocomplete',
  campaignCreators,
  selectedCreator,
  onChange,
  openOnFocus = false,
  autoFocus = false,
  selectTextOnFocus = false,
}: CreatorAutocompleteProps) {
  const options: Option[] = useMemo(
    () =>
      campaignCreators.map((creator) => ({
        id: creator.id,
        label: creator.name,
      })),
    [campaignCreators]
  );

  const selectedOption: Option | null = selectedCreator
    ? {id: selectedCreator.id, label: selectedCreator.name}
    : null;

  const handleChange = (event: React.SyntheticEvent, value: Option | null) => {
    if (value) {
      const creator = campaignCreators.find(
        (candidate) => candidate.id === value.id
      );
      onChange(creator ?? null, 'select');
      return;
    }

    // MUI fires the same `clear` change whether the input was emptied by typing
    // or by the clear button; only the event tells them apart.
    onChange(null, event.type === 'change' ? 'searchTextEmptied' : 'clear');
  };

  return (
    <AutocompleteInput
      key={selectedCreator?.id ?? 'empty'}
      testID={testID}
      options={options}
      selectedOptions={selectedOption}
      onChange={handleChange}
      placeholder={translate('Search by creator name...')}
      disableCloseOnSelect={false}
      openOnFocus={openOnFocus}
      autoFocus={autoFocus}
      selectTextOnFocus={selectTextOnFocus}
    />
  );
}

export default CreatorAutocomplete;
